import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, CategoryResponse } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-catalog-categories-panel',
  imports: [
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    DialogModule,
    TagModule,
    AinCanAccessDirective
  ],
  template: `
    <ain-info-hint
      [titleKey]="'catalog.categories.hint.title'"
      [descriptionKey]="'catalog.categories.hint.description'" />

    @if (loading()) {
      <ain-state type="loading" [title]="t('catalog.loading')" />
    } @else {
      <section class="toolbar">
        <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'catalog.categories.create'" [label]="t('catalog.categories.create')" (click)="openCreate()"></button>
      </section>

      <p-table [value]="categories()" [paginator]="categories().length > 20" [rows]="20">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('columns.name') }}</th>
            <th>{{ t('catalog.categories.path') }}</th>
            <th>{{ t('catalog.categories.consumptionClassification') }}</th>
            <th>{{ t('catalog.categories.lockState') }}</th>
            <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.name }}</td>
            <td>{{ row.path }}</td>
            <td>{{ t(classificationKey(row.consumptionClassification)) }}</td>
            <td>
              @if (row.isCodingLocked) {
                <p-tag severity="warn" [value]="t('catalog.categories.locked')" />
              } @else {
                <p-tag severity="success" [value]="t('catalog.categories.editable')" />
              }
            </td>
            <td class="actions">
              <button pButton type="button" size="small" severity="secondary" ainCanAccess [ainCanAccessPermission]="'catalog.categories.update'" [label]="t('common.edit')" (click)="openEdit(row)"></button>
              <button pButton type="button" size="small" severity="danger" ainCanAccess [ainCanAccessPermission]="'catalog.categories.delete'" [label]="t('common.delete')" [disabled]="row.isCodingLocked" (click)="remove(row)"></button>
            </td>
          </tr>
        </ng-template>
      </p-table>

      @if (error()) {
        <p class="error">{{ error() }}</p>
      }
    }

    <p-dialog [(visible)]="dialogVisible" [header]="dialogMode() === 'create' ? t('catalog.categories.create') : t('catalog.categories.edit')" [modal]="true" [style]="{ width: '520px' }">
      <div class="dialog-grid">
        <label><span>{{ t('columns.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
        <label><span>{{ t('catalog.categories.slug') }}</span><input pInputText [(ngModel)]="form.slug" [disabled]="editing()?.isCodingLocked ?? false" /></label>
        <label>
          <span>{{ t('catalog.categories.parent') }}</span>
          <p-select
            [(ngModel)]="form.parentId"
            [options]="parentOptions()"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            [disabled]="editing()?.isCodingLocked ?? false"
            appendTo="body" />
        </label>
        <label>
          <span>{{ t('catalog.categories.consumptionClassification') }}</span>
          <p-select
            [(ngModel)]="form.consumptionClassification"
            [options]="classificationOptions()"
            optionLabel="label"
            optionValue="value"
            [disabled]="editing()?.isCodingLocked ?? false"
            appendTo="body" />
        </label>
        @if (editing()?.isCodingLocked) {
          <ain-info-hint [titleKey]="'catalog.categories.lockHint.title'" [descriptionKey]="'catalog.categories.lockHint.description'" />
        }
        @if (dialogMode() === 'edit' && editing(); as current) {
          <div class="image-field">
            <span>{{ t('catalog.categories.image') }}</span>
            <ain-info-hint [titleKey]="'catalog.categories.imageHint.title'" [descriptionKey]="'catalog.categories.imageHint.description'" />
            <div class="image-row">
              @if (imagePreviewUrl(); as previewUrl) {
                <img class="image-preview" [src]="previewUrl" [alt]="current.name" />
              }
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" [disabled]="imageBusy()" (change)="onImageSelected($event)" />
              @if (current.imageFileId) {
                <button pButton type="button" size="small" severity="danger" [text]="true" [label]="t('catalog.categories.removeImage')" [disabled]="imageBusy()" (click)="removeImage()"></button>
              }
            </div>
          </div>
        }
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" ainCanAccess [ainCanAccessPermission]="dialogMode() === 'create' ? 'catalog.categories.create' : 'catalog.categories.update'" (click)="save()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; justify-content: flex-end; align-items: center; margin: 12px 0; gap: 8px; flex-wrap: wrap; }
    .actions { display: flex; gap: 6px; }
    .dialog-grid { display: grid; gap: 10px; }
    .dialog-grid label { display: grid; gap: 6px; }
    .error { color: #b91c1c; margin-top: 10px; }
    .image-field { display: grid; gap: 6px; }
    .image-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .image-preview { width: 96px; height: 72px; object-fit: contain; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; }
  `]
})
export class CatalogCategoriesPanelComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly categories = signal<CategoryResponse[]>([]);
  readonly dialogMode = signal<'create' | 'edit'>('create');
  readonly editing = signal<CategoryResponse | null>(null);
  readonly imageBusy = signal(false);
  dialogVisible = false;

  form = {
    name: '',
    slug: '',
    parentId: null as string | null,
    consumptionClassification: 'Unspecified'
  };

  ngOnInit(): void {
    this.load();
  }

  parentOptions(): Array<{ label: string; value: string | null }> {
    const editingId = this.editing()?.id;
    return [
      { label: this.t('catalog.categories.noParent'), value: null },
      ...this.categories()
        .filter(item => item.id !== editingId)
        .map(item => ({ label: item.path, value: item.id }))
    ];
  }

  classificationOptions(): Array<{ label: string; value: string }> {
    return [
      { label: this.t('catalog.categories.classification.unspecified'), value: 'Unspecified' },
      { label: this.t('catalog.categories.classification.consumable'), value: 'Consumable' },
      { label: this.t('catalog.categories.classification.nonConsumable'), value: 'NonConsumable' }
    ];
  }

  classificationKey(value: string): string {
    return `catalog.categories.classification.${value.charAt(0).toLowerCase()}${value.slice(1)}`;
  }

  openCreate(): void {
    this.dialogMode.set('create');
    this.editing.set(null);
    this.form = { name: '', slug: '', parentId: null, consumptionClassification: 'Unspecified' };
    this.dialogVisible = true;
  }

  openEdit(row: CategoryResponse): void {
    this.dialogMode.set('edit');
    this.editing.set(row);
    this.form = {
      name: row.name,
      slug: row.slug,
      parentId: row.parentId,
      consumptionClassification: row.consumptionClassification
    };
    this.dialogVisible = true;
  }

  save(): void {
    this.saving.set(true);
    this.error.set('');
    const payload = {
      name: this.form.name.trim(),
      slug: this.form.slug.trim(),
      consumptionClassification: this.form.consumptionClassification,
      parentId: this.form.parentId
    };
    const request = this.dialogMode() === 'create'
      ? this.service.createCategory(payload)
      : this.service.updateCategory(this.editing()!.id, payload);

    request.subscribe({
      next: () => {
        this.saving.set(false);
        this.dialogVisible = false;
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.categories.saveFailed'));
      }
    });
  }

  imagePreviewUrl(): string | null {
    const current = this.editing();
    return current ? this.service.categoryImagePreviewUrl(current) : null;
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const current = this.editing();
    if (!file || !current) {
      return;
    }
    this.imageBusy.set(true);
    this.error.set('');
    this.service.uploadCategoryImage(current.id, file).subscribe({
      next: updated => {
        input.value = '';
        this.applyImageChange(updated);
      },
      error: () => {
        input.value = '';
        this.imageBusy.set(false);
        this.error.set(this.t('catalog.categories.imageUploadFailed'));
      }
    });
  }

  removeImage(): void {
    const current = this.editing();
    if (!current) {
      return;
    }
    this.imageBusy.set(true);
    this.service.removeCategoryImage(current.id).subscribe({
      next: updated => this.applyImageChange(updated),
      error: () => {
        this.imageBusy.set(false);
        this.error.set(this.t('catalog.categories.imageUploadFailed'));
      }
    });
  }

  private applyImageChange(updated: CategoryResponse): void {
    this.imageBusy.set(false);
    this.editing.set(updated);
    this.categories.update(rows => rows.map(row => (row.id === updated.id ? updated : row)));
  }

  remove(row: CategoryResponse): void {
    this.service.deleteCategory(row.id).subscribe({
      next: () => this.load(),
      error: () => this.error.set(this.t('catalog.categories.deleteFailed'))
    });
  }

  private load(): void {
    this.loading.set(true);
    this.service.listCategories().subscribe({
      next: rows => {
        this.categories.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(this.t('catalog.categories.loadFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
